import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import type {
  Affiliate,
  AffiliateStats,
  Referral,
  AffiliateCommission,
  AffiliatePayout,
  AffiliateEarningsDataPoint,
  AffiliateActivityDataPoint,
  AffiliateApplication,
  AffiliateLink,
  CreateLinkRequest,
  UpdateLinkRequest,
} from "../types";
import {
  type DateRangeValue,
  isCustomRange,
} from "@/components/ui/date-range-selector";

import { API_BASE_URL as API_BASE } from "@/backend/config";

// Convert date range to days parameter for API
function dateRangeToDays(dateRange: DateRangeValue): number | null {
  if (dateRange === "all") return null;
  
  if (isCustomRange(dateRange)) {
    // Calculate days from custom range
    const days = Math.ceil(
      (dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24)
    );
    return days || 1;
  }
  
  // Map presets to approximate days
  switch (dateRange) {
    case "today":
    case "yesterday":
      return 1;
    case "this_week":
    case "last_week":
    case "last_7_days":
      return 7;
    case "this_month":
    case "last_month":
    case "last_30_days":
      return 30;
    case "last_90_days":
      return 90;
    case "ytd":
      return 365;
    default:
      return null;
  }
}

// Transform snake_case API response to camelCase frontend types
function transformAffiliate(apiAffiliate: Record<string, any>): Affiliate {
  return {
    id: apiAffiliate.id as string,
    userId: apiAffiliate.user_id as string,
    status: apiAffiliate.status as any,
    refCode: apiAffiliate.ref_code as string,
    vanitySlug: apiAffiliate.vanity_slug,
    displayName: apiAffiliate.display_name,
    tradingFeeShare: parseFloat(apiAffiliate.trading_fee_share),
    performanceBonusRate: parseFloat(apiAffiliate.performance_bonus_rate || apiAffiliate.revshare_percentage || "0"),
    referralDiscount: parseFloat(apiAffiliate.referral_discount),
    referralBonus: parseFloat(apiAffiliate.referral_bonus),
    signupBonusEnabled: apiAffiliate.signup_bonus_enabled || false,
    applicationNote: apiAffiliate.application_note,
    rejectionReason: apiAffiliate.rejection_reason,
    createdAt: apiAffiliate.created_at,
    approvedAt: apiAffiliate.approved_at,
  };
}

// Transform referral from snake_case API response to camelCase
function transformReferral(apiReferral: Record<string, any>): Referral {
  return {
    id: apiReferral.id as string,
    affiliateId: apiReferral.affiliate_id as string,
    referredUserId: apiReferral.referred_user_id as string,
    createdAt: apiReferral.created_at as string,
    email: apiReferral.email,
    walletAddress: apiReferral.wallet_address,
    totalVolume: apiReferral.total_volume ? parseFloat(apiReferral.total_volume) : 0,
    totalCommissions: apiReferral.total_commissions ? parseFloat(apiReferral.total_commissions) : 0,
  };
}

// Transform stats from snake_case API response to camelCase
function transformStats(apiStats: Record<string, any>): AffiliateStats {
  return {
    // Period-filtered stats
    periodEarnings: parseFloat(apiStats.period_earnings ?? apiStats.periodEarnings ?? "0"),
    periodReferrals: apiStats.period_referrals ?? apiStats.periodReferrals ?? 0,
    periodVolume: parseFloat(apiStats.period_volume ?? apiStats.periodVolume ?? "0"),
    periodClicks: apiStats.period_clicks ?? apiStats.periodClicks ?? 0,
    periodWithdrawals: parseFloat(apiStats.period_withdrawals ?? apiStats.periodWithdrawals ?? "0"),
    periodConversionRate: parseFloat(apiStats.period_conversion_rate ?? apiStats.periodConversionRate ?? "0"),
    // All-time reference stats
    allTimeEarnings: parseFloat(apiStats.all_time_earnings ?? apiStats.allTimeEarnings ?? "0"),
    pendingPayout: parseFloat(apiStats.pending_payout ?? apiStats.pendingPayout ?? "0"),
    paidOut: parseFloat(apiStats.paid_out ?? apiStats.paidOut ?? "0"),
  };
}

// Hook to get affiliate data for current user
export function useAffiliateData(dateRange: DateRangeValue = "all") {
  const { userId, isAuthenticated } = useUnifiedAuth();
  const days = dateRangeToDays(dateRange);

  const { data: affiliate, isLoading: isLoadingAffiliate } = useQuery({
    queryKey: ["affiliate", userId],
    queryFn: async (): Promise<Affiliate | null> => {
      const response = await fetch(`${API_BASE}/api/affiliates/me?user_id=${userId}`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Failed to fetch affiliate data");
      const data = await response.json();
      return data.affiliate ? transformAffiliate(data.affiliate) : null;
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 30000,
  });

  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ["affiliate-stats", userId, dateRange],
    queryFn: async (): Promise<AffiliateStats> => {
      const url = new URL(`${API_BASE}/api/affiliates/stats`);
      url.searchParams.set("user_id", userId || "");
      if (days !== null) {
        url.searchParams.set("days", days.toString());
      }
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error("Failed to fetch affiliate stats");
      const data = await response.json();
      const rawStats = data.stats || data;
      return transformStats(rawStats);
    },
    enabled: isAuthenticated && !!userId && affiliate?.status === "active",
    staleTime: 30000,
  });

  const { data: referrals = [], isLoading: isLoadingReferrals } = useQuery({
    queryKey: ["affiliate-referrals", userId],
    queryFn: async (): Promise<Referral[]> => {
      const response = await fetch(`${API_BASE}/api/affiliates/referrals?user_id=${userId}`);
      if (!response.ok) throw new Error("Failed to fetch referrals");
      const data = await response.json();
      const rawReferrals = data.referrals || data || [];
      return Array.isArray(rawReferrals) ? rawReferrals.map(transformReferral) : [];
    },
    enabled: isAuthenticated && !!userId && affiliate?.status === "active",
    staleTime: 30000,
  });

  const { data: commissions = [], isLoading: isLoadingCommissions } = useQuery({
    queryKey: ["affiliate-commissions", userId],
    queryFn: async (): Promise<AffiliateCommission[]> => {
      const response = await fetch(`${API_BASE}/api/affiliates/commissions?user_id=${userId}`);
      if (!response.ok) throw new Error("Failed to fetch commissions");
      const data = await response.json();
      return data.commissions || data; // Handle both wrapped and unwrapped responses
    },
    enabled: isAuthenticated && !!userId && affiliate?.status === "active",
    staleTime: 30000,
  });

  const { data: payouts = [], isLoading: isLoadingPayouts } = useQuery({
    queryKey: ["affiliate-payouts", userId],
    queryFn: async (): Promise<AffiliatePayout[]> => {
      const response = await fetch(`${API_BASE}/api/affiliates/payouts?user_id=${userId}`);
      if (!response.ok) throw new Error("Failed to fetch payouts");
      const data = await response.json();
      return data.payouts || data; // Handle both wrapped and unwrapped responses
    },
    enabled: isAuthenticated && !!userId && affiliate?.status === "active",
    staleTime: 30000,
  });

  const { data: earningsData = [], isLoading: isLoadingEarnings } = useQuery({
    queryKey: ["affiliate-earnings-chart", userId, dateRange],
    queryFn: async (): Promise<AffiliateEarningsDataPoint[]> => {
      const url = new URL(`${API_BASE}/api/affiliates/earnings`);
      url.searchParams.set("user_id", userId || "");
      if (days !== null) {
        url.searchParams.set("days", days.toString());
      }
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error("Failed to fetch earnings data");
      const data = await response.json();
      const rawEarnings = data.earnings || data || [];
      // Transform string values to numbers (backend returns Decimal as strings)
      return Array.isArray(rawEarnings) ? rawEarnings.map((e: Record<string, unknown>) => ({
        date: e.date as string,
        tradingFees: parseFloat(String(e.tradingFees)) || 0,
        performanceBonus: parseFloat(String(e.performanceBonus)) || 0,
        total: parseFloat(String(e.total)) || 0,
      })) : [];
    },
    enabled: isAuthenticated && !!userId && affiliate?.status === "active",
    staleTime: 60000,
  });

  const { data: activityData = [], isLoading: isLoadingActivity } = useQuery({
    queryKey: ["affiliate-activity-chart", userId, dateRange],
    queryFn: async (): Promise<AffiliateActivityDataPoint[]> => {
      const url = new URL(`${API_BASE}/api/affiliates/activity`);
      url.searchParams.set("user_id", userId || "");
      if (days !== null) {
        url.searchParams.set("days", days.toString());
      }
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error("Failed to fetch activity data");
      const data = await response.json();
      const rawActivity = data.activity || data || [];
      // Transform string values to numbers (backend returns Decimal as strings)
      return Array.isArray(rawActivity) ? rawActivity.map((a: Record<string, unknown>) => ({
        date: a.date as string,
        clicks: parseInt(String(a.clicks)) || 0,
        referrals: parseInt(String(a.referrals)) || 0,
        conversionRate: parseFloat(String(a.conversionRate)) || 0,
      })) : [];
    },
    enabled: isAuthenticated && !!userId && affiliate?.status === "active",
    staleTime: 60000,
  });

  return {
    affiliate,
    stats,
    referrals,
    commissions,
    payouts,
    earningsData,
    activityData,
    isLoading: isLoadingAffiliate,
    isLoadingStats,
    isLoadingReferrals,
    isLoadingCommissions,
    isLoadingPayouts,
    isLoadingEarnings,
    isLoadingActivity,
  };
}

// Hook for affiliate application
export function useAffiliateApplication() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (application: AffiliateApplication): Promise<Affiliate> => {
      const response = await fetch(`${API_BASE}/api/affiliates/apply?user_id=${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(application),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to submit application");
      }
      const data = await response.json();
      return data.affiliate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate", userId] });
    },
  });
}

// Hook for updating vanity slug
export function useUpdateVanitySlug() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (slug: string): Promise<Affiliate> => {
      const response = await fetch(`${API_BASE}/api/affiliates/vanity-slug?user_id=${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ slug }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update vanity slug");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate", userId] });
    },
  });
}

// Hook for requesting payout
export function useRequestPayout() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async ({ amount, address }: { amount: number; address: string }): Promise<AffiliatePayout> => {
      const response = await fetch(`${API_BASE}/api/affiliates/request-payout?user_id=${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount: amount.toString(), payoutAddress: address }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to request payout");
      }
      const data = await response.json();
      return data.payout;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-payouts", userId] });
      queryClient.invalidateQueries({ queryKey: ["affiliate-stats", userId] });
    },
  });
}

// Hook for updating referral discount
export function useUpdateReferralDiscount() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (discount: number): Promise<Affiliate> => {
      const response = await fetch(`${API_BASE}/api/affiliates/referral-discount?user_id=${userId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ referralDiscount: discount.toString() }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update referral discount");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate", userId] });
    },
  });
}

// ============================================================================
// Affiliate Link Hooks (Campaign Tracking)
// ============================================================================

// Transform link from API response
function transformLink(apiLink: Record<string, any>): AffiliateLink {
  return {
    id: apiLink.id as string,
    affiliateId: apiLink.affiliateId as string,
    name: apiLink.name as string,
    slug: apiLink.slug as string,
    referralDiscount: apiLink.referralDiscount ? parseFloat(apiLink.referralDiscount) : null,
    description: apiLink.description,
    isDefault: apiLink.isDefault as boolean,
    isActive: apiLink.isActive as boolean,
    createdAt: apiLink.createdAt as string,
    clickCount: parseInt(apiLink.clickCount) || 0,
    referralCount: parseInt(apiLink.referralCount) || 0,
    conversionRate: parseFloat(apiLink.conversionRate) || 0,
    tradingFeeEarnings: parseFloat(apiLink.tradingFeeEarnings) || 0,
    performanceBonusEarnings: parseFloat(apiLink.performanceBonusEarnings || apiLink.revshareEarnings || "0"),
    totalEarnings: parseFloat(apiLink.totalEarnings) || 0,
    affiliateDefaultDiscount: apiLink.affiliateDefaultDiscount ? parseFloat(apiLink.affiliateDefaultDiscount) : undefined,
    affiliateTradingFeeShare: apiLink.affiliateTradingFeeShare ? parseFloat(apiLink.affiliateTradingFeeShare) : undefined,
  };
}

// Hook to get affiliate links with stats
export function useAffiliateLinks() {
  const { userId, isAuthenticated } = useUnifiedAuth();

  return useQuery({
    queryKey: ["affiliate-links", userId],
    queryFn: async (): Promise<AffiliateLink[]> => {
      const response = await fetch(`${API_BASE}/api/affiliates/links?user_id=${userId}`);
      if (!response.ok) throw new Error("Failed to fetch affiliate links");
      const data = await response.json();
      return (data.links || []).map(transformLink);
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 30000,
  });
}

// Hook to create a new affiliate link
export function useCreateLink() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (request: CreateLinkRequest): Promise<AffiliateLink> => {
      const response = await fetch(`${API_BASE}/api/affiliates/links?user_id=${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create link");
      }
      const data = await response.json();
      return transformLink(data.link);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-links", userId] });
    },
  });
}

// Hook to update an affiliate link
export function useUpdateLink() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async ({ linkId, ...request }: UpdateLinkRequest & { linkId: string }): Promise<AffiliateLink> => {
      const response = await fetch(`${API_BASE}/api/affiliates/links/${linkId}?user_id=${userId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update link");
      }
      const data = await response.json();
      return transformLink(data.link);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-links", userId] });
    },
  });
}

// Hook to delete an affiliate link
export function useDeleteLink() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (linkId: string): Promise<void> => {
      const response = await fetch(`${API_BASE}/api/affiliates/links/${linkId}?user_id=${userId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete link");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-links", userId] });
    },
  });
}

// Hook to update affiliate display name
export function useUpdateDisplayName() {
  const queryClient = useQueryClient();
  const { userId } = useUnifiedAuth();

  return useMutation({
    mutationFn: async (displayName: string): Promise<Affiliate> => {
      const response = await fetch(`${API_BASE}/api/affiliates/display-name?user_id=${userId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ display_name: displayName }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update display name");
      }
      const data = await response.json();
      return transformAffiliate(data.affiliate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate", userId] });
    },
  });
}
